import React from "react";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { useFrame } from "../../shared/frame";
import { REDUCED_MOTION_QUERY } from "../../shared/reducedMotion";
import {
    AXIS,
    CY,
    HALO_RADIUS,
    HALO_RADIUS_PHONE,
    HALO_STOPS,
    LOOPS,
    RY,
    TOTAL,
    VIEWBOX_HEIGHT,
    VIEWBOX_WIDTH,
    areasOpened,
    createHalo,
    createLaps,
    dotPosition,
    loopShape,
    newLap,
    progress,
    stepHalo,
} from "./loops";

import styles from "./index.module.scss";

const DOT_COUNT = 3;
const PHONE_QUERY = "(max-width: 767px)";
const OPEN = [1, 1, 1, 1];
const CLOSED = [0, 0, 0, 0];

type Phase = "closed" | "opening" | "settled";

interface Props {
    /** The circles gate: true opens the loops, false folds them back */
    open: boolean;
    /** The section is on screen: dots and halo run only then */
    inView: boolean;
    reduced: boolean;
    /** How many areas' copy should show, as the loops pass halfway */
    onOpened: (count: number) => void;
}

/**
 * The four loops, their travelling dots and the orange halo under them. The
 * geometry is written straight to the elements on the frame loop; React only
 * holds the phase.
 */
export const LoopGraphic = (props: Props) => {
    const gradientId = `expertise-halo-${React.useId()}`;
    const svgRef = React.useRef<SVGSVGElement>(null);
    const gradientRef = React.useRef<SVGRadialGradientElement>(null);
    const wireRefs = React.useRef<(SVGEllipseElement | null)[]>([]);
    const dotRefs = React.useRef<(SVGCircleElement | null)[]>([]);
    const onOpenedRef = React.useRef(props.onOpened);
    React.useLayoutEffect(() => {
        onOpenedRef.current = props.onOpened;
    });

    // Rendered settled, so the server markup (and no-JS) shows the graphic
    const [phase, setPhase] = React.useState<Phase>("settled");
    const [phone, setPhone] = React.useState(false);
    const anim = React.useRef<{ t0: number; opened: number }>({
        t0: 0,
        opened: LOOPS.length,
    });
    const laps = React.useRef(createLaps(DOT_COUNT));
    const dotsArmed = React.useRef(false);
    const halo = React.useRef(createHalo());

    const placeLoops = (ps: readonly number[]) => {
        wireRefs.current.forEach((el, i) => {
            if (!el) return;
            const { rx, cx, opacity } = loopShape(i, ps[i]);
            el.setAttribute("rx", rx.toFixed(2));
            el.setAttribute("cx", cx.toFixed(2));
            el.style.opacity = opacity.toFixed(3);
        });
    };

    const reportOpened = (count: number) => {
        if (anim.current.opened === count) return;
        anim.current.opened = count;
        onOpenedRef.current(count);
    };

    // The gate, applied before paint so a re-arm never flashes the old state
    React.useLayoutEffect(() => {
        dotsArmed.current = false;
        // Read the query directly: the hook's state is still false on the
        // first client render, and closing the loops there would flash them
        if (props.reduced || window.matchMedia(REDUCED_MOTION_QUERY).matches) {
            placeLoops(OPEN);
            reportOpened(LOOPS.length);
            // eslint-disable-next-line react-hooks/set-state-in-effect -- the gate (and reduced motion) drives the phase
            setPhase("settled");
        } else if (props.open) {
            anim.current.t0 = performance.now();
            placeLoops(CLOSED);
            reportOpened(0);
            setPhase("opening");
        } else {
            placeLoops(CLOSED);
            reportOpened(0);
            setPhase("closed");
        }
    }, [props.open, props.reduced]);

    // Laps are timed against the clock, so after a long time off screen the
    // dots would all be due at the seam at once: deal them out afresh
    React.useEffect(() => {
        if (props.inView) dotsArmed.current = false;
    }, [props.inView]);

    React.useEffect(() => {
        const query = window.matchMedia(PHONE_QUERY);
        const update = () => setPhone(query.matches);
        update();
        query.addEventListener("change", update);
        return () => query.removeEventListener("change", update);
    }, []);

    // The halo leans toward the pointer, except on a phone, where the only
    // pointer is a finger dragging the page
    React.useEffect(() => {
        const state = halo.current;
        if (phone || props.reduced) {
            state.tx = state.cx = AXIS;
            state.ty = state.cy = CY;
            gradientRef.current?.setAttribute("cx", String(AXIS));
            gradientRef.current?.setAttribute("cy", String(CY));
            return;
        }
        const onMove = (event: PointerEvent) => {
            const rect = svgRef.current?.getBoundingClientRect();
            if (!rect?.width || !rect.height) return;
            state.tx =
                ((event.clientX - rect.left) / rect.width) * VIEWBOX_WIDTH;
            state.ty =
                ((event.clientY - rect.top) / rect.height) * VIEWBOX_HEIGHT;
        };
        window.addEventListener("pointermove", onMove, { passive: true });
        return () => window.removeEventListener("pointermove", onMove);
    }, [phone, props.reduced]);

    useFrame(
        (now, dt) => {
            if (phase === "opening") {
                const elapsed = now - anim.current.t0;
                placeLoops(LOOPS.map((_, i) => progress(i, elapsed)));
                reportOpened(areasOpened(elapsed));
                if (elapsed >= TOTAL) {
                    placeLoops(OPEN);
                    setPhase("settled");
                }
            }

            if (!phone) {
                const { cx, cy } = stepHalo(halo.current, dt);
                gradientRef.current?.setAttribute("cx", cx.toFixed(1));
                gradientRef.current?.setAttribute("cy", cy.toFixed(1));
            }

            if (phase === "settled") {
                if (!dotsArmed.current) {
                    laps.current.forEach((lap) =>
                        newLap(lap, laps.current, now, true),
                    );
                    dotsArmed.current = true;
                }
                laps.current.forEach((lap, k) => {
                    const el = dotRefs.current[k];
                    if (!el) return;
                    const { x, y } = dotPosition(lap, laps.current, now);
                    el.setAttribute("cx", x.toFixed(2));
                    el.setAttribute("cy", y.toFixed(2));
                });
            }
        },
        !props.reduced && (phase === "opening" || props.inView),
    );

    const settled = phase === "settled";
    const haloOn = settled && (props.inView || props.reduced);
    const dotsOn = settled && props.inView && !props.reduced;

    return (
        <svg
            ref={svgRef}
            className={styles.graphic}
            viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
        >
            <defs>
                <radialGradient
                    ref={gradientRef}
                    id={gradientId}
                    gradientUnits="userSpaceOnUse"
                    cx={AXIS}
                    cy={CY}
                    r={phone ? HALO_RADIUS_PHONE : HALO_RADIUS}
                >
                    {HALO_STOPS.map((stop) => (
                        <stop
                            key={stop.offset}
                            offset={stop.offset}
                            stopColor={stop.colour}
                            stopOpacity={stop.opacity}
                        />
                    ))}
                </radialGradient>
            </defs>
            <g
                className={concatClassNames([
                    styles.halo,
                    haloOn ? styles.on : "",
                ])}
            >
                <rect
                    x={-400}
                    y={-300}
                    width={2240}
                    height={1940}
                    fill={`url(#${gradientId})`}
                />
            </g>
            {LOOPS.map((loop, i) => (
                <ellipse
                    key={i}
                    ref={(el) => {
                        wireRefs.current[i] = el;
                    }}
                    className={styles.wire}
                    cx={AXIS + loop.side * loop.rx}
                    cy={CY}
                    rx={loop.rx}
                    ry={RY}
                />
            ))}
            {Array.from({ length: DOT_COUNT }, (_, k) => (
                <circle
                    key={k}
                    ref={(el) => {
                        dotRefs.current[k] = el;
                    }}
                    className={concatClassNames([
                        styles.dot,
                        dotsOn ? styles.on : "",
                    ])}
                    r={4}
                    cx={AXIS}
                    cy={CY}
                />
            ))}
        </svg>
    );
};
